import React from 'react';
import styled from 'styled-components';
import { Body1, Body5, CheckCircle, Container, H5, Page } from '../../libs/nvstr-common-ui.es';
import { useNavigate } from 'react-router';
import { ROUTES } from '../../constants/routes';
import BackArrowButton from '../../components/buttons/BackArrowButton';
import { FlatButton } from '../../components/buttons';
import { TextUploadForm } from '../form/TextUploadForm';
import { AI_TOOLS } from '../../constants';
import { useCurrentUserToolsAvailable, usePartnerTheme } from '../../hooks/user/useCurrentUser';
import WebpageUploader from '../../pages/WebpageUploader';
import { useSearchParams } from 'react-router-dom';
import { FileUploader } from './FileUploader';
import { PDFUploadForm } from '../form/PDFUploadForm';
import { FormState } from '../../components/UI/FormState';
import { sendMultipartApiRequest } from '../../services/api';
import { logNetRequest } from '../../utils/usefulFuncs';

const ButtonStyler = styled.div`
  button {
    width: 200px;
  }
`;
const CheckWrapper = styled.div`
  svg {
    height: 16px;
    width: 16px;

    path {
      fill: ${({ theme }) => theme.themeColors.text};
    }
  }
`;
const FormWrapper = styled.div`
  input {
    width: 220px;
  }

  button {
    width: 280px;
  }
`;

const UPLOAD_TYPES = {
  web: 'website',
  text: 'text',
  file: 'file',

  ocr: 'ocr',

  reupload: 'reupload',
  reprocess: 'reprocess',
};

function TextUploader({ onGoBack }) {
  return (
    <Page>
      <BackArrowButton onClick={onGoBack} />
      <Container top={36}>
        <h3>Text Document Uploader</h3>
        <TextUploadForm />
      </Container>
    </Page>
  );
}

function PDFDocUploader({ fedDoc, ocrDoc, adobeOcrDoc }) {
  return <PDFUploadForm fedDoc={fedDoc} ocrDoc={ocrDoc} adobeOcrDoc={adobeOcrDoc} />;
}

function OCRDocUploader({ onGoBack }) {
  return (
    <Page>
      <BackArrowButton onClick={onGoBack} />
      <Container top={36}>
        <PDFDocUploader ocrDoc />
      </Container>
    </Page>
  );
}

function FileReprocessor({ onGoBack }) {
  const [docId, setDocId] = React.useState('');
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [errorMessage, setErrorMessage] = React.useState(null);
  const [wasSuccess, setWasSuccess] = React.useState(false);

  const onChange = (e) => {
    setDocId(e.target.value);
    setWasSuccess(false);
  };

  const onSubmit = async () => {
    if (docId === '') {
      return;
    }

    setIsSubmitting(true);
    let formData = new FormData();
    formData.append('doc_id', docId);
    const URL = 'v1/genai_process_user_doc_reupload';
    const response = await sendMultipartApiRequest('post', URL, formData);
    logNetRequest(response?.status, response?.data, response?.error);
    if (response?.status !== 200) {
      setErrorMessage('Something went wrong, check logs for details');
    } else {
      setWasSuccess(true);
    }
    setIsSubmitting(false);
  };

  return (
    <Page>
      <BackArrowButton onClick={onGoBack} />
      <FormWrapper>
        <Container top={36}>
          <Container>
            <H5>Re-Process Document</H5>
          </Container>
          <Container top={16} row>
            <Container right={16}>
              <Body5>Doc ID:</Body5>
            </Container>
            <input value={docId} onChange={onChange} />
          </Container>
          <Container top={24}>
            <FormState isSubmitting={isSubmitting} error={errorMessage} leftAligned />

            <Container top={8}>
              <FlatButton onClick={onSubmit}>Submit</FlatButton>
            </Container>
          </Container>
          {wasSuccess && (
            <Container
              top={16}
              style={{
                width: '280px',
              }}
            >
              <Container centerAll height={48}>
                <CheckWrapper>
                  <CheckCircle />
                </CheckWrapper>
                <Container left={8} bottom={3}>
                  <Body5 bold>Request was successful</Body5>
                </Container>
              </Container>
            </Container>
          )}
        </Container>
      </FormWrapper>
    </Page>
  );
}

export const DocUploader = ({ onShowWidePage }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [uploadType, setUploadType] = React.useState(null);

  const toolsAvailable = useCurrentUserToolsAvailable();
  const isWebsiteUploadToolAvailable = toolsAvailable.includes(AI_TOOLS.WEBPAGE_UPLOAD);
  const isOCRUploadToolAvailable = toolsAvailable.includes(AI_TOOLS.EXCEL_UPLOAD);
  const isReUploadToolAvailable = toolsAvailable.includes(AI_TOOLS.ADMIN_DOC_REUPLOAD);

  const partnerTheme = usePartnerTheme();
  const isCustomClient = partnerTheme === 1;

  React.useEffect(() => {
    const typeParam = searchParams.get('type');
    if (typeParam) {
      const validTypes = Object.values(UPLOAD_TYPES);
      if (validTypes.includes(typeParam)) {
        if (uploadType !== typeParam) {
          setUploadType(typeParam);
        }
      }
    }
  }, [searchParams, uploadType]);

  React.useEffect(() => {
    if (uploadType === UPLOAD_TYPES.file) {
      onShowWidePage(true);
    } else {
      onShowWidePage(false);
    }
  }, [uploadType]);

  const onGoBack = () => navigate(ROUTES.home);

  if (uploadType === UPLOAD_TYPES.text) {
    return <TextUploader onGoBack={() => setUploadType(null)} />;
  }
  if (uploadType === UPLOAD_TYPES.file) {
    return <FileUploader onGoBack={() => setUploadType(null)} />;
  }
  if (uploadType === UPLOAD_TYPES.web) {
    return <WebpageUploader onBackClick={() => setUploadType(null)} />;
  }
  if (uploadType === UPLOAD_TYPES.ocr) {
    return <OCRDocUploader onGoBack={() => setUploadType(null)} fedDoc />;
  }
  if (uploadType === UPLOAD_TYPES.reupload) {
    return <FileUploader onGoBack={() => setUploadType(null)} reupload />;
  }
  if (uploadType === UPLOAD_TYPES.reprocess) {
    return <FileReprocessor onGoBack={() => setUploadType(null)} />;
  }

  return (
    <Page>
      <BackArrowButton onClick={onGoBack} />
      <Container top={40}>
        <Body1 bold>Upload Document</Body1>
        {isCustomClient ? (
          <Container top={8}>
            <Body5>Warning: Please upload only public information to FedGPT</Body5>
          </Container>
        ) : null}
        <Container top={16} bottom={24}>
          <ButtonStyler>
            {isOCRUploadToolAvailable && (
              <Container top={24}>
                <FlatButton onClick={() => setUploadType(UPLOAD_TYPES.ocr)}>OCR</FlatButton>
              </Container>
            )}

            <Container top={24}>
              <FlatButton onClick={() => setUploadType(UPLOAD_TYPES.file)}>File</FlatButton>
            </Container>
            <Container top={24}>
              <FlatButton onClick={() => setUploadType(UPLOAD_TYPES.text)}>Text</FlatButton>
            </Container>
            {isWebsiteUploadToolAvailable && (
              <Container top={24}>
                <FlatButton onClick={() => setUploadType(UPLOAD_TYPES.web)}>Website</FlatButton>
              </Container>
            )}
            {isReUploadToolAvailable && (
              <Container top={24}>
                <FlatButton onClick={() => setUploadType(UPLOAD_TYPES.reupload)}>Re-Upload</FlatButton>
              </Container>
            )}
            {isReUploadToolAvailable && (
              <Container top={24}>
                <FlatButton onClick={() => setUploadType(UPLOAD_TYPES.reprocess)}>Re-Process</FlatButton>
              </Container>
            )}
          </ButtonStyler>
        </Container>
      </Container>
    </Page>
  );
};
